import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, Save, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const FIXED_FIELDS = [
  {
    key: "pba_receipt_url",
    label: "Monthly Financial Compliance — PBA Receipt Link",
    desc: "Destination URL for the contractor's 'Submit PBA Receipt' Monthly Financial Compliance tile.",
  },
  {
    key: "dspd_safety_url",
    label: "DSPD Compliance Safety Reference Link",
    desc: "External state documentation (Utah DSPD or equivalent).",
  },
  {
    key: "incident_report_url",
    label: "Incident Report Form Link",
    desc: "URL opened by the contractor 'Submit Incident Report' utility tile.",
  },
];

const newRowId = () => `cl_${Math.random().toString(36).slice(2, 10)}`;

export default function AdminLinks() {
  const [links, setLinks] = useState({ custom_links: [] });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/system-links").then(({ data }) => {
      const customLinks = Array.isArray(data.custom_links)
        ? data.custom_links.map((c) => ({ ...c, id: c.id || newRowId() }))
        : [];
      setLinks({ ...data, custom_links: customLinks });
    });
  }, []);

  const update = (k, v) => {
    setLinks((l) => ({ ...l, [k]: v }));
    setDirty(true);
  };

  const addCustom = () => {
    update("custom_links", [...(links.custom_links || []), { id: newRowId(), title: "", url: "" }]);
  };
  const updateCustom = (id, patch) => {
    update(
      "custom_links",
      (links.custom_links || []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );
  };
  const removeCustom = (id) => {
    update("custom_links", (links.custom_links || []).filter((c) => c.id !== id));
  };

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = (links.custom_links || [])
        .filter((c) => (c.title || "").trim() && (c.url || "").trim())
        .map((c) => ({ id: c.id, title: c.title.trim(), url: c.url.trim() }));
      const { data } = await api.put("/system-links", {
        pba_receipt_url: links.pba_receipt_url || "",
        dspd_safety_url: links.dspd_safety_url || "",
        incident_report_url: links.incident_report_url || "",
        custom_links: cleaned,
      });
      setLinks({
        ...data,
        custom_links: (data.custom_links || []).map((c) => ({ ...c, id: c.id || newRowId() })),
      });
      setDirty(false);
      toast.success("Global links saved — deployed live to every active session.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const customLinks = links.custom_links || [];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-links">
      <div>
        <p className="text-label">Admin Settings</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Global URL &amp; Link Manager</h1>
        <p className="text-sm text-muted-foreground">
          Central mapping for every external hyperlink used across the system. Updates deploy instantly without a code rebuild.
        </p>
      </div>

      <Card className="p-6 space-y-5">
        {FIXED_FIELDS.map(({ key, label, desc }) => (
          <div key={key} className="space-y-1.5">
            <Label htmlFor={key} className="flex items-center gap-2 font-semibold">
              <Link2 className="w-4 h-4 text-primary" /> {label}
            </Label>
            <Input
              id={key}
              type="url"
              value={links[key] || ""}
              onChange={(e) => update(key, e.target.value)}
              placeholder="https://..."
              data-testid={`link-${key}`}
            />
            <p className="text-xs text-muted-foreground">{desc}</p>
          </div>
        ))}
      </Card>

      {/* v28 — Agency-wide custom titled links */}
      <Card className="p-6 space-y-3" data-testid="custom-links-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            <div>
              <p className="text-label">Agency-wide Custom Links</p>
              <p className="text-[11px] text-muted-foreground">
                Add any extra external resource. Assign a clear title and URL — these appear in the contractor Resources hub.
              </p>
            </div>
          </div>
          <Button onClick={addCustom} size="sm" variant="outline" data-testid="add-custom-link">
            <Plus className="w-4 h-4 mr-1" /> Add link
          </Button>
        </div>
        {customLinks.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-3">No custom links yet. Click &quot;Add link&quot; to start.</p>
        )}
        {customLinks.map((c) => (
          <div key={c.id} className="grid md:grid-cols-[260px_1fr_auto_auto] gap-2 items-center" data-testid={`custom-link-${c.id}`}>
            <Input
              placeholder="Title (e.g., Utah DDD Provider Manual)"
              value={c.title || ""}
              onChange={(e) => updateCustom(c.id, { title: e.target.value })}
              data-testid={`custom-link-title-${c.id}`}
            />
            <Input
              type="url"
              placeholder="https://..."
              value={c.url || ""}
              onChange={(e) => updateCustom(c.id, { url: e.target.value })}
              data-testid={`custom-link-url-${c.id}`}
            />
            <Button variant="outline" size="sm" disabled={!c.url} asChild>
              <a href={c.url || "#"} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => removeCustom(c.id)} data-testid={`remove-custom-link-${c.id}`}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        ))}
      </Card>

      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={save} disabled={!dirty || busy} data-testid="save-links">
          <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
